import { forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { ColorSystemPositive1, ColorSystemPositive2 } from 'autogen/design-tokens/tokens';
import type { FollowBrandClubModel } from 'autogen/swagger/all';
import {
    GetFollowBrandClubModelQueryKey,
    useBrandClubSignoffMutation,
    useBrandClubSignupMutation,
    useGetDrawerForMemberQuery,
    useGetDrawerForNotMemberQuery,
    useGetFollowBrandClubModelQuery
} from 'autogen/swagger/all/hooks';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import { ButtonWithIcon } from 'components/Button';
import Icon from 'components/Icon';
import { useOpenLoginQuickView } from 'components/login/shared/hooks/useLoginInfoQuickView';
import { QuickViewWithAbsoluteHeader, useQuickView, useSlides } from 'components/quickView';
import toast from 'components/Toast';
import dynamic from 'helpers/dynamic';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import useGA4Tracking from 'modules/tracking/GA4';
import queryClient from 'setup/react-query/queryClient';
import { v4 as uuidv4 } from 'uuid';

const MemberContent = dynamic(() => import('./components/quickView/MemberContent'));
const NonMemberContent = dynamic(() => import('./components/quickView/NonMemberContent'));

type Animate = 'in' | 'out' | null;

const success = css`
    background-color: ${ColorSystemPositive1};
    border-color: ${ColorSystemPositive2};
    color: white;

    & svg {
        fill: white;
    }
`;

const initial = css`
    background-color: initial;
    border-color: initial;
    color: initial;

    & svg {
        fill: initial;
    }
`;

const buttonAnimation = ($animate: Animate) => keyframes`
    from {
        ${$animate === 'in' ? initial : success}
    }

    to {
        ${$animate === 'in' ? success : initial}
    }
`;

const StyledButton = styled(ButtonWithIcon)<{ $animate: Animate }>`
    
    &, &:hover, &:active, &:focus {
        background-color: white;
    }

    ${({ $animate }) => {
        if ($animate !== null) {
            return css`
                animation-name: ${buttonAnimation($animate)};
                animation-duration: ${$animate === 'in' ? 300 : 1000}ms;
                animation-timing-function: ease;
                animation-fill-mode: both;
            `;
        }
    }}
`;

export type FollowButtonProps = FollowBrandClubModel;

const FollowButton = forwardRef<HTMLButtonElement, FollowButtonProps>((props, ref) => {
    const nonMemberQuickViewId = useMemo(() => uuidv4(), []);
    const memberQuickViewId = useMemo(() => uuidv4(), []);

    // General hooks
    const { t } = useTranslation();
    const location = useLocation();
    const { slideProps } = useSlides();
    const isAuthenticated = useIsAuthenticated();
    const GA4Tracking = useGA4Tracking();
    const openLoginInfo = useOpenLoginQuickView({ infoType: 'General', level: 'Hard', returnUrl: location.pathname });

    // State
    const [animation, animate] = useState<Animate>(null);
    const { data } = useGetFollowBrandClubModelQuery({ brandClubId: props.brandClubId }, { enabled: isAuthenticated });
    const { isFollowing, brandClubId, brandClubName } = data ?? props;

    // QuickViews
    const { toggle: toggleNonMemberQuickView } = useQuickView(nonMemberQuickViewId);
    const { toggle: toggleMemberQuickView } = useQuickView(memberQuickViewId);

    // Mutations
    const { data: nonMemberData, refetch: refetchNonMemberData } = useGetDrawerForNotMemberQuery(
        { brandClubId },
        { enabled: false }
    );
    const { data: memberData, refetch: refetchMemberData } = useGetDrawerForMemberQuery(
        { brandClubId },
        { enabled: false }
    );
    const { mutateAsync: signup } = useBrandClubSignupMutation();
    const { mutateAsync: signoff } = useBrandClubSignoffMutation();

    function closeDrawerTracking() {
        GA4Tracking({
            eventName: 'close_drawer',
            category: 'navigation',
            eventParams: [
                {
                    param_name: 'follow_brand_name',
                    param_value: brandClubName
                }
            ]
        });
    }

    // Functions
    function onClick() {
        if (!isAuthenticated) {
            openLoginInfo();
        } else {
            if (isFollowing) {
                refetchMemberData();
                toggleMemberQuickView(true);
            } else {
                refetchNonMemberData();
                toggleNonMemberQuickView(true);
            }

            GA4Tracking({
                eventName: 'open_drawer',
                category: 'navigation',
                eventParams: [{ param_name: 'follow_brand_name', param_value: brandClubName }]
            });
        }
    }

    async function toggleFollow() {
        const newState = !isFollowing;

        try {
            if (newState === true) {
                await signup({ params: { AcceptTerms: true, Id: brandClubId } });

                animate('in');

                setTimeout(() => {
                    animate('out');
                }, 3000);

                toggleNonMemberQuickView(false);
            } else {
                await signoff({ params: { id: brandClubId } });

                toggleMemberQuickView(false);
            }
        } catch (err) {
            console.error(err);
            toast.error(t(GeneralExceptionError));
        }

        GA4Tracking({
            eventName: 'submit',
            category: 'ecommerce',
            eventParams: [
                { param_name: 'follow_brand_name', param_value: brandClubName },
                { param_name: 'follow_brand_submit', param_value: newState ? 'follow' : 'unfollow' }
            ]
        });

        queryClient.invalidateQueries({ queryKey: [GetFollowBrandClubModelQueryKey] });
    }

    return (
        <>
            <StyledButton
                ref={ref}
                size="sm"
                icon={isFollowing ? <Icon type="ChevronDown" size={16} /> : <Icon type="Plus" size={16} />}
                iconProperties={{ position: isFollowing ? 'right' : 'left' }}
                variant="outline"
                onClick={onClick}
                $animate={animation}
            >
                {isFollowing ? 'Følger' : 'Følg'}
            </StyledButton>
            <QuickViewWithAbsoluteHeader
                quickViewId={memberQuickViewId}
                position="right"
                {...slideProps}
                onClose={closeDrawerTracking}
            >
                {memberData && <MemberContent confirmCallback={toggleFollow} data={memberData} />}
            </QuickViewWithAbsoluteHeader>
            <QuickViewWithAbsoluteHeader
                quickViewId={nonMemberQuickViewId}
                position="right"
                {...slideProps}
                onClose={closeDrawerTracking}
            >
                {nonMemberData && <NonMemberContent confirmCallback={toggleFollow} data={nonMemberData} />}
            </QuickViewWithAbsoluteHeader>
        </>
    );
});

export default FollowButton;
